import React, { useCallback, useRef, useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaCloudUploadAlt, FaCheckCircle, FaQrcode, FaCamera } from 'react-icons/fa';
import { uploadEventPhotos, getManagerPhotographers, getEvent, getMySubscription } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { galleryStorageQuota, formatStorage } from '../utils/galleryStorage';
import { isEventManager } from '../utils/roles';

const EVENT_PHOTO_MAX_SOURCE_BYTES = 30 * 1024 * 1024;

const EventPhotos = () => {
  const { eventId } = useParams();
  const [files, setFiles] = useState([]);
  const submitting = useRef(false);
  const [subscription, setSubscription] = useState(null);
  const [checkingStorage, setCheckingStorage] = useState(true);
  const [storageError, setStorageError] = useState('');
  const [serverStorageError, setServerStorageError] = useState('');
  const refreshStorage = useCallback(async () => {
    setCheckingStorage(true);
    setStorageError('');
    try {
      const current = await getMySubscription();
      if (!galleryStorageQuota(current)) throw new Error('Missing storage quota');
      setSubscription(current);
      return current;
    } catch (err) {
      setStorageError('Impossible de vérifier votre espace disponible. Actualisez votre quota avant de réessayer.');
      throw err;
    } finally {
      setCheckingStorage(false);
    }
  }, []);
  useEffect(() => { refreshStorage().catch(() => {}); }, [refreshStorage]);
  const quota = galleryStorageQuota(subscription, files);
  const [isArchived, setIsArchived] = useState(null);
  const [selfieSearchEnabled, setSelfieSearchEnabled] = useState(null);
  useEffect(() => {
    let active = true;
    setSelfieSearchEnabled(null);
    setIsArchived(null);
    getEvent(eventId).then((response) => {
      if (active) {
        const event = response.event || response.data?.event || response;
        setSelfieSearchEnabled(Boolean(event.selfie_search_enabled));
        setIsArchived(Boolean(event.is_archived));
      }
    }).catch(() => { if (active) setError("Impossible de charger les paramètres de l'événement."); });
    return () => { active = false; };
  }, [eventId]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();

  // A manager credits each batch to the photographer who shot it; a
  // photographer uploading their own event is always credited themselves, so
  // they never see this picker.
  const managesRoster = isEventManager(user);
  const [roster, setRoster] = useState([]);
  const [photographerId, setPhotographerId] = useState('');

  useEffect(() => {
    if (!managesRoster) return;

    getManagerPhotographers()
      .then(response => setRoster(response.photographers || []))
      .catch(() => setError("Impossible de charger votre équipe de photographes"));
  }, [managesRoster]);

  const handleFileChange = (e) => {
    if (isArchived !== false) return;
    setServerStorageError('');
    const selectedFiles = Array.from(e.target.files);
    
    setError('');
    // Reject oversized originals instead of silently recompressing them.
    const validFiles = selectedFiles.filter(file => {
      if (!file.type.startsWith('image/')) {
        setError('Seuls les fichiers image sont autorisés');
        return false;
      }
      if (file.size > EVENT_PHOTO_MAX_SOURCE_BYTES) {
        setError('Chaque fichier doit faire moins de 30 Mo');
        return false;
      }
      return true;
    });

    setFiles(validFiles);
  };

  const handleUpload = async () => {
    if (isArchived !== false || submitting.current || checkingStorage || storageError || serverStorageError || !quota || quota.exceeded) return;
    if (files.length === 0) {
      setError('Veuillez sélectionner au moins une photo');
      return;
    }

    submitting.current = true;
    setServerStorageError('');
    setUploading(true);
    setError('');
    setSuccess('');
    setUploadProgress(0);

    try {
      const response = await getEvent(eventId);
      if ((response.event || response.data?.event || response).is_archived) {
        setIsArchived(true);
        return;
      }
      const fresh = galleryStorageQuota(await refreshStorage(), files);
      if (fresh.exceeded) return;
      const uploadedPhotos = [];

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        setUploadProgress(Math.round((index / files.length) * 100));

        const formData = new FormData();
        // Send the selected File directly: preserve its bytes, dimensions and embedded metadata.
        formData.append('photos[]', file);
        formData.append('selfie_search_enabled', selfieSearchEnabled ? '1' : '0');
        formData.append('original_names[]', file.name);
        // Omitted, the API credits the event's owner.
        if (photographerId) {
          formData.append('photographer_id', photographerId);
        }

        const response = await uploadEventPhotos(eventId, formData);
        uploadedPhotos.push(...(response.photos || []));
        setFiles(previous => previous.filter(selected => selected !== file));
        setUploadProgress(Math.round(((index + 1) / files.length) * 100));
      }

      const totalFaces = uploadedPhotos.reduce((sum, photo) => sum + (photo.faces_detected || 0), 0);

      setSuccess(
        totalFaces > 0
          ? `${uploadedPhotos.length} photos importées avec succès ! ${totalFaces} visages détectés et associés aux utilisateurs.`
          : `${uploadedPhotos.length} photos importées avec succès.`
      );
      setFiles([]);
      setUploadProgress(100);

      setTimeout(() => {
        navigate(`/event/${eventId}/browse`);
      }, 3000);
    } catch (err) {
      const data = err.response?.data || err;
      if (data.code === 'STORAGE_LIMIT_EXCEEDED') {
        setServerStorageError(data.message);
      } else {
        setError(data.errors?.subscription?.[0] || data.message || "L’import a échoué. Veuillez réessayer.");
      }
    } finally {
      await refreshStorage().catch(() => {});
      submitting.current = false;
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    setServerStorageError('');
    setFiles(files.filter((_, i) => i !== index));
  };

  if (isArchived) return (
    <div className="container">
      <section className="upload-container" style={{ maxWidth: 650 }} aria-labelledby="archived-upload-title">
        <h2 id="archived-upload-title">Galerie archivée</h2>
        <p role="status">L’import de photos est désactivé. Réactivez cette galerie depuis vos événements avant d’ajouter de nouvelles photos.</p>
        <Link to="/photographer" className="btn-primary">Mes événements</Link>
        <p><Link to="/pricing">Comparer les forfaits</Link></p>
      </section>
    </div>
  );

  return (
    <div className="container">
      <div className="upload-container" style={{ maxWidth: '800px' }}>
        <h2 style={{ textAlign: 'center', color: 'var(--primary-orange)', marginBottom: '1rem' }}>
          Importer les photos de l'événement
        </h2>
        <p style={{ textAlign: 'center', color: 'var(--gray)', marginBottom: '2rem' }}>
          Importez les photos de votre événement. Notre IA détectera automatiquement les visages et les associera aux utilisateurs inscrits.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
          <Link
            to={`/event/${eventId}/qr`}
            className="btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
          >
            <FaQrcode /> Generer le QR code PDF
          </Link>
        </div>

        <div role="status" style={{ padding: '1rem', background: 'var(--bg)', borderRadius: '8px', marginBottom: '1rem' }}>
          <strong>Votre espace de stockage actif</strong>
          {quota && <p>{formatStorage(quota.used)} utilisés sur {formatStorage(quota.limit)} · {formatStorage(quota.remaining)} disponibles</p>}
          {quota && files.length > 0 && <p>Photos sélectionnées : {formatStorage(quota.selected)}</p>}
          {checkingStorage && <p>Vérification de votre espace…</p>}
          {storageError && <p role="alert">{storageError}</p>}
          <button type="button" disabled={uploading || checkingStorage} onClick={() => { setServerStorageError(''); refreshStorage().catch(() => {}); }}>Actualiser mon espace</button>
        </div>
        {(quota?.exceeded || serverStorageError) && <div role="alert" className="error-message">
          <strong>Un peu plus d’espace pour vos photos</strong>
          <p>{serverStorageError || 'Votre espace disponible ne suffit pas pour cet import. Vos photos déjà importées sont conservées. Choisissez un forfait supérieur ou retirez des photos de votre sélection.'}</p>
          <Link to="/pricing" className="btn-primary">Augmenter mon stockage</Link>
        </div>}
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {managesRoster && (
          <div style={{
            marginBottom: '2rem',
            padding: '1.25rem',
            background: 'var(--bg)',
            borderRadius: '12px',
            border: '2px solid #A855F7',
          }}>
            <label htmlFor="batchPhotographer" style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
              <FaCamera /> Photographe de ce lot
            </label>
            {roster.length === 0 ? (
              <p style={{ color: 'var(--gray)', margin: 0 }}>
                Votre équipe est vide.{' '}
                <Link to="/manager/photographers">Ajoutez un photographe</Link> pour
                pouvoir lui attribuer des photos.
              </p>
            ) : (
              <>
                <select
                  id="batchPhotographer"
                  value={photographerId}
                  onChange={(e) => setPhotographerId(e.target.value)}
                  disabled={uploading || selfieSearchEnabled === null}
                  style={{ width: '100%', padding: '0.7rem', borderRadius: '8px' }}
                >
                  <option value="">Moi-même (organisateur)</option>
                  {roster.map(photographer => (
                    <option key={photographer.id} value={photographer.id}>
                      {photographer.name} — {photographer.email}
                    </option>
                  ))}
                </select>
                <small style={{ color: 'var(--gray)', display: 'block', marginTop: '0.5rem' }}>
                  Toutes les photos de cet import lui seront attribuées. Vous
                  pourrez modifier cette attribution ensuite depuis la galerie.
                </small>
              </>
            )}
          </div>
        )}

        <div className="form-group">
          <label><input type="checkbox" checked={selfieSearchEnabled === true}
            disabled={uploading || selfieSearchEnabled === null}
            onChange={(e) => setSelfieSearchEnabled(e.target.checked)} /> Activer la recherche de photos par selfie</label>
          <p>Ce choix s'applique à tout l'événement, y compris aux photos déjà importées.</p>
        </div>

        <div
          className="upload-zone"
          onClick={() => document.getElementById('multiFileInput').click()}
        >
          <div className="upload-icon">
            <FaCloudUploadAlt />
          </div>
          <h3>Cliquez pour sélectionner des photos</h3>
          <p style={{ marginTop: '0.5rem' }}>Vous pouvez sélectionner plusieurs photos à la fois</p>
          <p style={{ fontSize: '0.9rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
            Formats acceptés : JPG, PNG, WEBP · 30 Mo maximum par photo. Les fichiers originaux sont conservés sans compression.
          </p>
        </div>

        <input
          id="multiFileInput"
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {files.length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>
              Photos sélectionnées ({files.length})
            </h3>
            
            <div style={{ 
              maxHeight: '300px', 
              overflowY: 'auto', 
              background: 'var(--bg)', 
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              {files.map((file, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.8rem',
                    background: 'var(--surface)',
                    marginBottom: '0.5rem',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px var(--shadow)'
                  }}
                >
                  <div>
                    <strong>{file.name}</strong>
                    <span style={{ color: 'var(--gray)', marginLeft: '1rem' }}>
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    style={{
                      background: '#EF4444',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Retirer
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={handleUpload}
              className="btn-submit"
              disabled={uploading || selfieSearchEnabled === null || checkingStorage || !!storageError || !quota || quota.exceeded || !!serverStorageError}
            >
              {uploading 
                ? `Import et traitement... ${uploadProgress}%`
                : `Importer ${files.length} photo${files.length > 1 ? 's' : ''}`}
            </button>
          </div>
        )}

        <div style={{ 
          marginTop: '2rem', 
          padding: '1.5rem', 
          background: 'var(--bg)', 
          borderRadius: '12px',
          border: '2px solid var(--light-green)'
        }}>
          <h4 style={{ color: 'var(--primary-green)', marginBottom: '1rem' }}>
            <FaCheckCircle /> Que se passe-t-il ensuite ?
          </h4>
          <ul style={{ color: 'var(--gray)', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
            <li>Les photos sont importées dans l'événement</li>
            <li>L'IA détecte automatiquement tous les visages de chaque photo</li>
            <li>Les visages sont comparés aux profils utilisateurs inscrits</li>
            <li>Les utilisateurs sont informés de leurs photos trouvées</li>
            <li>Des scores de confiance sont calculés pour chaque correspondance</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default EventPhotos;
